import {AfterViewInit, Component, inject, OnDestroy, OnInit, signal, TemplateRef, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {ApiService} from '../../_services/api.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {FormElement, FormPage, SoaMapping, SpecialMapping, WebFormTemplate} from './models/WebFormTemplate';
import { HttpClient } from '@angular/common/http';
import {CustomFormControl} from './components/controls/CustomFormControl';
import {slideInOutAnimation} from '../../_animations/animations';
import {WebFormSummary} from './models/WebFormSummary';
import {FormHelperService} from './services/form-helper.service';
import {SendDocumentResponse} from './models/api/SendDocumentResponse';
import {ViewportScroller} from '@angular/common';
import {UserInfo} from './models/UserInfo';
import {AttachmentDto} from './models/AttachmentDto';
import {lastValueFrom, Subscription} from 'rxjs';
import {FormSerializerService} from './services/form-serializer.service';
import {FormFAQ} from './models/api/FormFAQ';
import {NgbTooltipConfig} from '@ng-bootstrap/ng-bootstrap';
import {BsModalRef, BsModalService} from 'ngx-bootstrap/modal';
import {FormDetail} from './models/api/Form';
import {FormOutlineComponent} from './components/form-outline/form-outline.component';
import {SubmissionEvent} from './models/SubmissionEvent';
import {FileOnlineViewModel} from './FileOnlineViewModel';
import {ModalAlertMessage} from './models/ModalAlertMessage';
import {Title} from '@angular/platform-browser';
import {EnvironmentService} from '../../_services/environment.service';
import {ConfigOption} from '../../_models/ConfigOption';
import {FormLoaderService} from './services/form-loader.service';
import {FormSummarizerService} from './services/form-summarizer.service';
import {SiteConfigService} from '../../_services/site-config.service';

export enum FormLoadState {
  loading,
  errored, // 404,500
  loaded
}

@Component({
    selector: 'app-file-online-v2',
    templateUrl: './file-online-v2.component.html',
    styleUrls: ['./file-online-v2.component.scss'],
    animations: [slideInOutAnimation],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class FileOnlineV2Component implements OnInit, AfterViewInit, OnDestroy {

  public readonly FormLoadState = FormLoadState;

  // Global view model
  viewModel = new FileOnlineViewModel();

  // Temp data that is specific to forms
  pageIndex = 0;
  formProgress = 0; // % progress
  outlineItemIndex = 0;
  outlineItems: string[] = [];
  formSummaries: WebFormSummary[] = [];
  siteSettings = new Map<string, string>();
  hasReturnToConfirmation = false;
  // When errors are encountered
  eFileDisabled = false; // If AMP offline or maintenance mode
  eFileDisabledToolTip: string | undefined;
  eFileSubmissionError: string | null;
  formLoadState = FormLoadState.loading;
  // Created when the form is loaded
  faq: FormFAQ;
  formDetail: FormDetail | null;
  formId: string;
  formPages: FormPage[] = [];
  templates = new Map<string, FormElement[]>();
  controls = new Map<string, CustomFormControl>();
  specialMappings: SpecialMapping[] = [];
  soaMappings: SoaMapping[] = [];
  // Signix page data
  isSignixFrameLoading = false;
  documentResponse: SendDocumentResponse | null;
  // Misc
  devMode = false; // Useful for debugging non-published forms
  submissionCount = 0;
  isSubmitting = false;
  userInfo: UserInfo | null;
  autofillValues = new Map<string, string>();
  useTemplateFromDatabase = true;
  modalRef: BsModalRef;
  @ViewChild(FormOutlineComponent) formOutline!: FormOutlineComponent;
  // Listen for modal
  private modalAlertSubscription: Subscription;
  @ViewChild('genericAlert') genericAlertTemplate: TemplateRef<HTMLInputElement>;

  previewJson = signal(null);

  private router = inject(Router);
  private formLoader = inject(FormLoaderService);
  private formSummarizer = inject(FormSummarizerService);
  protected siteConfig = inject(SiteConfigService);

  constructor(private apiService: ApiService,
              private route: ActivatedRoute,
              private http: HttpClient,
              public formHelper: FormHelperService,
              private formSerializer: FormSerializerService,
              private viewportScroller: ViewportScroller,
              private ngbToolTipService: NgbTooltipConfig,
              private modalService: BsModalService,
              private titleService: Title,
              private environmentService: EnvironmentService) {}

  ngOnInit(): void {
    this.listenForQueryParamChanges();
    this.listenForModalAlerts();

    // Note: Apparently, NgbToolTip requires the presence of an enclosing
    // tag container with value 'body'. You can add container="body" to
    // individual <input> elements OR you can set it globally in the config.
    this.ngbToolTipService.container = 'body';
  }

  ngAfterViewInit(): void {
    window.addEventListener('resize', this.onWindowResize);
  }

  ngOnDestroy(): void {
    window.removeEventListener('resize', this.onWindowResize);
    this.modalAlertSubscription.unsubscribe();
  }

  private listenForModalAlerts(): void {
    this.modalAlertSubscription = this.viewModel.modalTriggerObservable.subscribe((message: ModalAlertMessage) => {
      this.viewModel.modalAlertMessage = message;
      this.modalRef = this.modalService.show(this.genericAlertTemplate);
    });
  }

  private listenForQueryParamChanges(): void {
    this.route.queryParams.subscribe((params: Params) => {
      if (params.dev) {
        this.devMode = true;
        if (params.page) {
          this.pageIndex = parseInt(params.page, 10);
        }
      }

      if (params.local) {
        this.useTemplateFromDatabase = false;
      }

      const formId = params.id;
      if (formId === undefined) { return; }

      this.formId = formId;
      // Capture the direct eFile URL after its query parameters are available.
      this.environmentService.rememberNavigatedUrl(globalThis.location.href);
      this.loadConfiguration(formId).then();
    });
  }

  // Load all the information needed to properly display the form.
  async loadConfiguration(formId: string): Promise<void> {
    try {
      // The form may be known by another name in the Assessor api
      const assessorApiFormattedFormId = this.getAssessorApiFormattedId(this.formId);

      // Load credentials, settings, templates
      const [ampUserInfo, siteSettings, formDetail, formFAQ] = await Promise.all([
        lastValueFrom(this.apiService.userInfo()),
        this.apiService.siteSettings().toPromise(),
        this.apiService.getForm(assessorApiFormattedFormId).toPromise(),
        this.apiService.getFormFaq(formId).toPromise()
      ]);

      // Process site specific settings (e.g., maintenance mode)
      this.processSiteSettings(siteSettings);
      // Cache AMP credentials to be autofilled when the form template is loaded
      this.processAmpCredentials(ampUserInfo);

      // Create the UI components from the data stored in the database or in the /assets/forms folder
      if (this.useTemplateFromDatabase) {
        const webFormTemplate = JSON.parse(formFAQ.formTemplate) as WebFormTemplate;
        this.processWebFormTemplate(formFAQ, formDetail, webFormTemplate);
      } else {
        const localWebFormTemplate = await this.http.get<WebFormTemplate>(`assets/forms/${formId.toUpperCase()}.json`).toPromise();
        this.processWebFormTemplate(formFAQ, formDetail, localWebFormTemplate);
      }

      if (formDetail.eFileMaintenance && !this.devMode) {
        this.disableEFile();
      }

      // // Load the AMP status
      // let ampIsOffline = false;
      // const ampStatusResponse = await this.apiService.getAmpStatus().toPromise();
      // if (ampStatusResponse instanceof PingDocScanResponse) {
      //   ampIsOffline = ampStatusResponse.statusCode !== 200;
      // } else if (ampStatusResponse.status !== 200) {
      //   ampIsOffline = true;
      // }
      //
      // if (ampIsOffline && !this.devMode) {
      //   this.disableEFile();
      // }
    } catch {
      this.formLoadState = FormLoadState.errored;

      if (!this.devMode) {
        this.disableEFile();
      }
    }
  }

  disableEFile(): void {
    this.eFileDisabled = true;
    this.eFileDisabledToolTip = 'The Assessor e-File system is currently down for maintenance. Please try again later.';
  }

  get getMaintenanceBanner(): string {
    if (!this.eFileDisabled) { return null; }
    const formSpecificMessage = this.formDetail?.eFileMaintenanceBanner;
    if (formSpecificMessage && formSpecificMessage.length > 0) { return formSpecificMessage; }
    if (this.siteConfig.isEFileSubmissionMaintenanceEnabled()) {
      return this.siteConfig.getEFileMaintenanceAlert();
    }
    return 'The Assessor e-File submission system is down for maintenance. Please check back soon. We apologize for any inconvenience caused.';
  }

  private processSiteSettings(options: ConfigOption[]): void {
    options.forEach(option => {
      this.siteSettings.set(option.setting, option.data);
    });
  }

  private processAmpCredentials(ampUserInfo: UserInfo): void {
    this.autofillValues.set('AMPUSERID', ampUserInfo.email); // Legacy support
    this.autofillValues.set('AMPLASTNAME', ampUserInfo.lastName);
    this.autofillValues.set('AMPMIDDLENAME', ampUserInfo.middleName);
    this.autofillValues.set('AMPFIRSTNAME', ampUserInfo.firstName);
    this.autofillValues.set('AMPMOBILENO', ampUserInfo.primaryPhone);
    this.userInfo = ampUserInfo;
  }

  private processWebFormTemplate(faq: FormFAQ, formDetail: FormDetail, webFormTemplate: WebFormTemplate): void {
    // Create the form and assign data
    const createdFormInfo = this.formLoader.createForm(webFormTemplate, faq, this.viewModel);
    this.formDetail = formDetail;
    this.faq = faq;
    this.formPages = createdFormInfo.formPages;
    this.templates = createdFormInfo.templates;
    this.controls = createdFormInfo.controls;
    this.specialMappings = createdFormInfo.specialMappings;
    this.soaMappings = webFormTemplate.soaMappings;

    // Update autofill if necessary
    this.fillInValues();
    // Update the outline
    this.updateOutlineItems();
    // Update the page title
    this.titleService.setTitle(`${this.environmentService.tabName()} - ` + faq.formName);

    const isMaintenanceMode = this.siteSettings.has('MaintenanceEFileSubmission');
    this.formLoadState = FormLoadState.loaded;

    // If this form is unpublished, or AMP is offline, or maintenance mode is enabled and devMode is NOT true, disable
    if ((!formDetail.hasEFile || isMaintenanceMode) && !this.devMode) {
      this.disableEFile();
    }
  }

  private getAssessorApiFormattedId(formNumber: string): string {
    switch (formNumber.toLowerCase()) {
      case 'boe-266-assr-515':
        return 'boe-266 / assr-515'; // We need to do this to properly load the FormDetail from the assessor api ;(
      case 'ads-820.2':
        return 'ads-820';
      default:
        return formNumber;
    }
  }

  private fillInValues(): void {
    // Attempt to auto fill
    this.controls.forEach((control: CustomFormControl) => {
      if (!control.element.autofillKey) { return; }
      const autoFillValue = this.autofillValues.get(control.element.autofillKey);
      if (!autoFillValue) { return; }
      control.setValue(autoFillValue, { emitEvent: false });
    });
  }

  // --------- GETTERS ---------

  getFormPage(type: string): FormPage | null {
    if (this.formPages.length === 0) { return null; }
    let page = this.formPages.length - 1;
    while (page > 0) {
      const formPage = this.formPages[page];
      if (formPage.type === type) { return formPage; }
      --page;
    }

    return null;
  }

  isPageVisible(): boolean {
    const formPage = this.formPages[this.pageIndex];
    return this.formHelper.isFormPageVisible(formPage, this.controls);
  }

  pageHasFormHints(): boolean {
    const formPage = this.formPages[this.pageIndex];
    return formPage && formPage.hints && formPage.hints.length > 0;
  }

  // --------- PAGE NAVIGATION ---------

  smoothScrollToTop(): void {
    this.viewportScroller.scrollToAnchor('formTop');
  }

  doesNotHavePrevious(): boolean {
    return this.pageIndex - 1 < 0;
  }

  isOnLastPage(): boolean {
    return this.pageIndex + 1 === this.formPages.length;
  }

  canProgressOrSubmit(): boolean {
    if (this.isOnLastPage()) { return; }

    const currentForm = this.formPages[this.pageIndex].formGroup;
    return currentForm.valid && !currentForm.pending && this.hasRequiredFiles();
  }

  hasRequiredFiles(): boolean {
    const formPage = this.formPages[this.pageIndex];

    for (const element of formPage.elements) {
      if (element.type !== 'file') { continue; }
      if (element.fileUploadOptional) { return true; }
      if (element.files.length === 0) {
        if (element.fileErrors.length === 0) {
          element.fileErrors = ['You must upload at least one file.'];
        }

        return false;
      }
    }

    return true;
  }

  navigateToPage(type: string): void {
    for (let i = 0; i < this.formPages.length; ++i) {
      if (this.formPages[i].type === type) {
        this.pageIndex = i;
      }
    }

    this.smoothScrollToTop();
    this.updateOutlineItems();
  }

  updateNavigationState(): void {
    this.hasReturnToConfirmation = false;
    this.smoothScrollToTop();
    // Rebuild the outline as views may have conditionally been enabled
    this.updateOutlineItems();
  }

  updateOutlineItems(): void {
    const progress = this.pageIndex / (this.formPages.length - 1) * 100.0;
    this.formProgress = Math.ceil(progress);

    const outlineItems: string[] = ['Welcome'];
    let isOnSignaturePage = false;
    let outlinePageIndex = 0;
    let selectedOutlinePageIndex = 0;

    for (let i = 0; i < this.formPages.length; ++i) {
      // Skip the landing page
      if (i === 0) { continue; }
      const formPage = this.formPages[i];
      // Skip pages that are not visible
      if (!this.formHelper.isFormPageVisible(formPage, this.controls)) { continue; }

      outlineItems.push(formPage.title);
      ++outlinePageIndex;

      if (i === this.pageIndex) {
        selectedOutlinePageIndex = outlinePageIndex;
      }

      // Do not include pages after the form confirmation
      if (formPage.type === 'form_preview') {
        outlineItems.push('Signature');
        isOnSignaturePage = this.pageIndex > i;
        break;
      }
    }

    if (isOnSignaturePage) {
      selectedOutlinePageIndex = this.outlineItems.length - 1;
    }

    this.outlineItems = outlineItems;
    this.outlineItemIndex = selectedOutlinePageIndex;

    setTimeout(() => {
      // Set up the initial highlight
      this.formOutline?.updateHighlightPosition();
    }, 50);
  }

  clearFileUploadErrors(): void {
    for (const element of this.formPages[this.pageIndex].elements) {
      element.fileErrors = [];
    }
  }

  // --------- EMITTERS, EVENTS AND BUTTONS ---------

  handlePreviousClicked(): void {
    while (this.pageIndex > 0) {
      this.pageIndex--;
      if (this.isPageVisible()) { break; }
    }

    this.clearFileUploadErrors();
    this.updateNavigationState();
  }

  handleConfirmSubmissionClicked(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template);
  }

  handleSubmissionConfirmedClicked(): void {
    if (this.isSubmitting) {
      return; // Prevent double submissions
    }

    this.modalRef.hide();
    this.handleNextClicked();
  }

  handleNextClicked(): void {
    const formGroup = this.formPages[this.pageIndex].formGroup;
    if (!this.canProgressOrSubmit()) {
      this.formHelper.markFormAsTouched(formGroup);
      return;
    }

    while (this.pageIndex < this.formPages.length) {
      this.pageIndex++;
      if (this.isPageVisible()) { break; }
    }

    this.clearFileUploadErrors();
    this.updateNavigationState();

    switch (this.formPages[this.pageIndex].type) {
      case 'form_summary':
        // Only build the summary when we navigate to it
        this.formSummaries = this.formSummarizer.buildWebFormSummaries(this.formPages, this.controls);
        break;
      case 'form_preview':
        this.generatePdfPreview();
        break;
      case 'form_loading':
        this.submitForm();
        break;
    }
  }

  handleModifyClicked(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.hasReturnToConfirmation = true;
    this.smoothScrollToTop();
    this.updateOutlineItems();
  }

  handleDownloadFormClicked(): void {
    window.open(this.faq.url, '_blank');
  }

  handleReturnBack(): void {
    if (!this.canProgressOrSubmit()) {
      return;
    }

    this.hasReturnToConfirmation = false;
    this.formSummaries = this.formSummarizer.buildWebFormSummaries(this.formPages, this.controls);
    this.navigateToPage('form_summary');
    this.updateOutlineItems();
  }

  handleResubmitClicked(): void {
    this.navigateToPage('form_loading');
    this.submitForm();
  }

  handleConfirmationClicked(): void {
    if (!this.canProgressOrSubmit()) {
      return;
    }

    this.navigateToPage('form_summary');
    this.updateOutlineItems();
  }

  handlePreviewClicked(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template);
    this.generatePdfPreview();
  }

  handleEFileInstructionsClicked(): void {
    if (this.formDetail.instructionUrl) {
      this.formHelper.openLinkInNewTab(this.formDetail.instructionUrl);
    }
  }

  handleReturnToFormsClicked(template: TemplateRef<any>): void {
    this.modalRef = this.modalService.show(template);
  }

  handleSignixTimeoutLinkClicked(): void {
    this.apiService.accessLink(this.documentResponse.onlineFormId).subscribe(signixLink => {
      if (this.formHelper.isSafari()) {
        this.formHelper.openLinkInTab(signixLink.link);
      } else {
        this.formHelper.openLinkInNewTab(signixLink.link);
      }
    });
  }

  handleConfirmReturnToFormsClicked(): void {
    this.modalRef.hide();
    this.router.navigate(['/forms']);
  }

  // --------- FORM SUBMISSION ---------

  generatePdfPreview(): void {
    const json = this.transformFormToJson();
    this.previewJson.set(json);
  }

  transformFormToJson(): string {
    return this.formSerializer.transformFormToJson(
      this.formId,
      this.faq.url,
      this.controls,
      this.formPages,
      this.specialMappings,
      this.soaMappings
    );
  }

  submitForm(): void {
    if (this.isSubmitting) {
      console.error('Form is already submitting');
      return;
    }

    this.submissionCount += 1;
    this.isSubmitting = true;

    console.log('Starting form submission');

    const formBody = this.transformFormToJson();
    const files: AttachmentDto[] = [];

    for (const formPage of this.formPages) {
      for (const formElement of formPage.elements) {
        if (!formElement.files) { continue; }

        for (const file of formElement.files) {
          files.push({ isPii: formElement.isPii, file });
        }
      }
    }

    this.apiService.submitForm(formBody, files).subscribe((res: SendDocumentResponse) => {
      this.documentResponse = res;
      if (this.formHelper.isSafari()) {
        this.formHelper.openLinkInTab(res.url);
      } else if (this.formHelper.isMobileDevice()) {
        this.formHelper.openLinkInNewTab(res.url);
      } else {
        const signixPage = this.getFormPage('signix_signature');
        if (!signixPage) { return; }
        this.isSignixFrameLoading = true;
        this.createSignaturePageTimeout();
        this.navigateToPage('signix_signature');
        this.updateOutlineItems();
      }
    }, error => {
      this.handleFormSubmissionError(error);
      this.logSubmissionFailure(null, 'Failed to submit form', error);
    });
  }

  createSignaturePageTimeout(): void {
    setTimeout(() => {
      if (this.isSignixFrameLoading) {
        this.isSignixFrameLoading = false;
      }
    }, 10000);
  }

  logSubmissionFailure(documentGUID: string, friendlyError: string, error: any): void {
    this.isSubmitting = false;

    const submissionEvent = new SubmissionEvent();
    submissionEvent.submitterEmail = this.userInfo?.email;
    submissionEvent.documentGuid = documentGUID;
    submissionEvent.friendlyError = friendlyError;
    submissionEvent.error = JSON.stringify(error);
    submissionEvent.userAgent = navigator.userAgent;
    submissionEvent.submissionCount = this.submissionCount;
    submissionEvent.webFormSubmission = this.transformFormToJson();

    this.apiService.notifySubmissionError(submissionEvent).subscribe();
  }

  handleFormSubmissionError(error: any): void {
    this.navigateToPage('form_error');

    if (error.status === 0) {
      this.eFileSubmissionError = 'Your authentication state is invalid. ' +
        'Please logout, open a new browser tab, and start a new submission.';
      return;
    }

    const friendlyError = error.error?.message ?? error.error;
    console.error(error.error);

    if (!friendlyError || typeof friendlyError === 'object') {
      this.eFileSubmissionError = 'The connection was reset. Please resubmit.';
      return;
    }

    // // Check for invalid emails
    // const invalidEmailPattern = /Value &apos;(.*?)&apos;/;
    // const invalidEmailMatch = friendlyError.match(invalidEmailPattern);
    // if (invalidEmailMatch) {
    //   this.eFileSubmissionError = `The provided email ${invalidEmailMatch[1]} is not a valid email.`;
    //   return;
    // }
    //
    // // Check for invalid values
    // const invalidParam = /parameter &apos;(.*?)&apos;/;
    // const invalidParamMatch = friendlyError.match(invalidParam);
    // if (invalidParamMatch) {
    //   const control = this.controls.get(invalidParamMatch[1]);
    //   this.eFileSubmissionError = `The input ${control?.value ?? ''} for ${invalidParamMatch[1]} is not valid. If this is a name, please remove any special characters.`;
    //   return;
    // }

    this.eFileSubmissionError = friendlyError;
  }

  // --------- JS EVENTS ---------

  onWindowResize = (): void => {
    this.formOutline?.updateHighlightPosition();
  }

  onSignixFrameLoad(): void {
    setTimeout(() => {
      this.isSignixFrameLoading = false;
    }, 1000);
  }
}
